import './SotGa4Labeling.scss';
import cn from 'classnames';
import { gzip } from 'pako';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Nav } from 'react-bootstrap';
import { MdDelete, MdErrorOutline, MdOutlineFileDownload, MdSave } from 'react-icons/md';
import { formatNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { AlertDialog } from '@Components/AlertDialog';
import { Button } from '@Components/Button';
import { Appearance, Intent, IntentType, Placement, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { FilterBar } from '@Components/FilterBar';
import { IconButton } from '@Components/IconButton';
import { InteractiveSpan } from '@Components/InteractiveEl';
import { FullPageSpinner } from '@Components/Spinner';
import { useAppSelector } from '../../redux';
import { useSetError } from '../../redux/modals';
import * as UserRedux from '../../redux/user';
import { Brand60 } from '../../utils/colors';
import { dbSotGa4, SotGa4EditRecord, SotGa4EditRecordKey } from '../../utils/db-sot-ga4';
import { downloadJSONToCSV } from '../../utils/download-utils';
import { S3Put, SegmentationMappingLambdaFetch } from '../../utils/fetch-utils';
import { useStateFunction } from '../../utils/hooks/useData';
import { useCompanyInfo } from '../../utils/hooks/useCompanyInfo';
import {
  conditionallyPluralize,
  conditionallyPluralizeWithCount,
  equalsIgnoreCase
} from '../../utils/text-utils';
import {
  CustomSegmentsData,
  SystemGeneratedSegmentNames
} from '../SegmentationMapping/segmentation-mapping-constants';
import {
  SotGa4BatchType,
  SotGa4BatchTypeValue,
  SotGa4EditsMap,
  SotGa4EditsMapAction,
  SotGa4EditsMapActionType,
  SotGa4MappingRow,
  SotGa4Row,
  SotGa4RowEdit
} from './sot-ga4-mapping-tool-constants';
import { SotGa4LabelingTable } from './SotGa4LabelingTable';
import { SotGa4PendingSegmentationChanges } from './SotGa4PendingSegmentationChanges';

const S3_BUCKET_LOCATION = 'bpm-cdn';

const SOURCE_LABEL_MAP: Record<string, string> = {
  custom_sot: 'Custom SoT',
  ga4: 'GA4',
  skai: 'Skai',
  tvad: 'TVAD',
  shopify: 'Shopify',
  custom_delivery: 'Custom Delivery',
  pacvue: 'Pacvue'
};

interface SotGa4UpdatedRow {
  // raw_analytics_dimensions_id. The backend used to re-derive row identity from the eight fields
  // below, but that hash covers eleven -- it also includes source and analytics_account_id. Rows
  // differing only in those collapsed onto one key, so all but the first edit was dropped and the
  // survivor's segmentation_id was written to every sibling row. Sending the key we already have
  // addresses exactly one row. (DEV-23421)
  campaign_key: string;
  ad_group_id?: string;
  ad_group_name?: string;
  ad_id?: string;
  ad_name?: string;
  campaign_id?: string;
  campaign_name?: string;
  raw_medium?: string;
  raw_source?: string;
  segmentMap: Record<string, string>;
}

interface LabelingProps {
  data: SotGa4MappingRow[] | null;
  customSegments: CustomSegmentsData[] | undefined;
  fetchRawData: () => Promise<void>;
}

const TabOptions = {
  LABELED: 'Labeled',
  UNLABELED: 'Unlabeled'
} as const;
type TabOptionsType = (typeof TabOptions)[keyof typeof TabOptions];

const TAB_OPTION_VALUES: readonly string[] = Object.values(TabOptions);
const isTabOption = (value: string | null): value is TabOptionsType =>
  value !== null && TAB_OPTION_VALUES.includes(value);

const STATIC_FILTER_BAR_OPTIONS = [
  { name: 'analytics_account_id', label: 'Ad Analytics ID' },
  { name: 'ad_group_id', label: 'Ad Group ID' },
  { name: 'ad_group_name', label: 'Ad Group' },
  { name: 'ad_id', label: 'Ad ID' },
  { name: 'ad_name', label: 'Ad Name' },
  { name: 'campaign_id', label: 'Campaign ID' },
  { name: 'campaign_name', label: 'Campaign' },
  { name: 'source_of_truth', label: 'Source of Truth' },
  { name: 'channel', label: 'Channel' },
  { name: 'platform', label: 'Platform' },
  { name: 'raw_medium', label: 'Medium' },
  { name: 'raw_source', label: 'Source' }
];

const computeUpdatedEditsMap = (
  currentEditsMap: SotGa4EditsMap,
  updates: { rows: SotGa4Row[]; edits: SotGa4RowEdit }[]
): { newMap: SotGa4EditsMap; changedKeys: Set<string> } => {
  const newEditsMap = { ...currentEditsMap };
  const changedKeys = new Set<string>();

  for (const update of updates) {
    const { rows, edits } = update;
    if (!rows) {
      throw new Error('No row provided for updating segment edit operation.');
    }
    if (!edits) {
      continue;
    }

    rows.forEach(row => {
      changedKeys.add(row.campaign_key);
      const existingRow = newEditsMap[row.campaign_key];

      const filteredEdits: SotGa4RowEdit = {};
      for (const segmentName of Object.keys(edits)) {
        const segmentEdit = edits[segmentName];
        const originalValue = row[segmentName.toLowerCase()];
        if (segmentEdit?.label !== originalValue) {
          filteredEdits[segmentName] = segmentEdit;
        }
      }

      if (Object.keys(filteredEdits).length === 0) {
        if (existingRow) {
          delete newEditsMap[row.campaign_key];
        }
        return;
      }

      if (!existingRow) {
        newEditsMap[row.campaign_key] = { ...row, edits: filteredEdits };
      } else {
        const updatedEdits = { ...(existingRow.edits || {}), ...filteredEdits };

        for (const segmentName of Object.keys(updatedEdits)) {
          const segmentEdit = updatedEdits[segmentName];
          const originalValue = row[segmentName.toLowerCase()];
          if (segmentEdit?.label === originalValue) {
            delete updatedEdits[segmentName];
          }
        }

        newEditsMap[row.campaign_key] = { ...existingRow, edits: updatedEdits };

        const rowEdits = newEditsMap[row.campaign_key]?.edits || {};
        if (Object.keys(rowEdits).length === 0) {
          delete newEditsMap[row.campaign_key];
        }
      }
    });
  }

  return { newMap: newEditsMap, changedKeys };
};

const REQUIRED_SEGMENT_NAMES: readonly string[] = [
  SystemGeneratedSegmentNames.CHANNEL,
  SystemGeneratedSegmentNames.PLATFORM
];

const CIDS_ALLOWING_UNDEFINED_REQUIRED_SEGMENTS: ReadonlySet<string> = new Set([
  'atriumhomeservices'
]);

const MISSING_SEGMENT_LABEL = 'Undefined';

const isResolvedSegmentValue = (value: unknown): boolean =>
  typeof value === 'string' &&
  value.trim().length > 0 &&
  !equalsIgnoreCase(value, MISSING_SEGMENT_LABEL);

const hasRequiredSegmentValues = (row: SotGa4Row): boolean =>
  REQUIRED_SEGMENT_NAMES.every(name => {
    const editLabel = row.edits?.[name]?.label;
    return editLabel !== undefined
      ? isResolvedSegmentValue(editLabel)
      : isResolvedSegmentValue(row[name.toLowerCase()]);
  });

interface DialogState {
  title: string;
  body: string;
  intent?: IntentType;
  escapable?: boolean;
  showFooter?: boolean;
}

export const SotGa4Labeling = ({ data, fetchRawData, customSegments }: LabelingProps) => {
  const { cid } = useCompanyInfo();
  const setError = useSetError();

  const allowsUndefinedRequiredSegments = CIDS_ALLOWING_UNDEFINED_REQUIRED_SEGMENTS.has(cid);

  const [labeledEditsMap, setLabeledEditsMap] = useState<SotGa4EditsMap>({});

  const [unlabeledEditsMap, setUnlabeledEditsMap] = useState<SotGa4EditsMap>({});
  const [selectedRows, setSelectedRows] = useState<Record<string, SotGa4Row>>({});
  const [activeTab, setActiveTab] = useState<TabOptionsType>(TabOptions.UNLABELED);

  const [dialog, setDialog] = useState<DialogState | null>(null);

  // Mirror of the edits-map state, so writers can compute the next map
  // synchronously. Deriving it inside a setState updater and reading the
  // result after the call is unsafe: React only runs updaters eagerly when
  // the queue is empty, so a second rapid edit would persist nothing.
  const editsMapsRef = useRef<Record<SotGa4BatchTypeValue, SotGa4EditsMap>>({
    labeled: {},
    unlabeled: {}
  });

  const applyEditsMap = useCallback((batchType: SotGa4BatchTypeValue, newMap: SotGa4EditsMap) => {
    editsMapsRef.current[batchType] = newMap;
    const setEditsMap =
      batchType === SotGa4BatchType.LABELED ? setLabeledEditsMap : setUnlabeledEditsMap;
    setEditsMap(newMap);
  }, []);

  const loadDataFromDexie = useCallback(async () => {
    try {
      const [labeledRecords, unlabeledRecords] = await Promise.all([
        dbSotGa4.editRecords
          .where('[cid+batchType]')
          .equals([cid, SotGa4BatchType.LABELED])
          .toArray(),
        dbSotGa4.editRecords
          .where('[cid+batchType]')
          .equals([cid, SotGa4BatchType.UNLABELED])
          .toArray()
      ]);

      const labeledMap: SotGa4EditsMap = {};
      const unlabeledMap: SotGa4EditsMap = {};

      labeledRecords.forEach(record => {
        labeledMap[record.campaignKey] = record.rowData;
      });

      unlabeledRecords.forEach(record => {
        unlabeledMap[record.campaignKey] = record.rowData;
      });

      applyEditsMap(SotGa4BatchType.LABELED, labeledMap);
      applyEditsMap(SotGa4BatchType.UNLABELED, unlabeledMap);
    } catch (error) {
      console.error('Error loading data from Dexie:', error);
    }
  }, [cid, applyEditsMap]);

  useEffect(() => {
    if (!cid) {
      return;
    }

    loadDataFromDexie();
  }, [cid, loadDataFromDexie]);

  const updateEdits = useCallback(
    async (
      updates: { rows: SotGa4Row[]; edits: SotGa4RowEdit }[],
      batchType: SotGa4BatchTypeValue
    ) => {
      if (!cid) {
        return;
      }

      const { newMap, changedKeys } = computeUpdatedEditsMap(
        editsMapsRef.current[batchType],
        updates
      );
      applyEditsMap(batchType, newMap);

      // Batch persist only changed records (single Dexie transaction instead of N)
      // bulkPut on the v2 schema upserts on [cid+campaignKey+batchType], so
      // re-issuing the same logical edit no longer accumulates duplicate rows.
      const now = Date.now();
      const recordsToWrite: SotGa4EditRecord[] = [];

      for (const campaignKey of Array.from(changedKeys)) {
        const rowData = newMap[campaignKey];
        if (rowData) {
          recordsToWrite.push({
            cid,
            campaignKey,
            batchType,
            rowData,
            timestamp: now
          });
        }
      }

      if (recordsToWrite.length > 0) {
        try {
          await dbSotGa4.editRecords.bulkPut(recordsToWrite);
        } catch (e) {
          setError({
            message: `Failed to persist edits locally — they may not survive a page refresh. Error: ${e.message}`,
            reportError: e
          });
        }
      }
    },
    [cid, applyEditsMap, setError]
  );

  const updateUnlabeledEdits = useCallback(
    (updates: { rows: SotGa4Row[]; edits: SotGa4RowEdit }[]) =>
      updateEdits(updates, SotGa4BatchType.UNLABELED),
    [updateEdits]
  );

  const updateLabeledEdits = useCallback(
    (updates: { rows: SotGa4Row[]; edits: SotGa4RowEdit }[]) =>
      updateEdits(updates, SotGa4BatchType.LABELED),
    [updateEdits]
  );

  const deleteSegmentEdit = useCallback(
    async (campaign_keys: string[], segmentName: string, batchType: SotGa4BatchTypeValue) => {
      if (!cid || !campaign_keys || !segmentName) {
        return;
      }

      const keysToDelete: SotGa4EditRecordKey[] = [];
      const recordsToUpsert: SotGa4EditRecord[] = [];
      const now = Date.now();

      const newEditsMap = { ...editsMapsRef.current[batchType] };

      campaign_keys.forEach(campaign_key => {
        const updatedRow = newEditsMap[campaign_key];
        if (!updatedRow) {
          return;
        }
        const updatedRowEdits = { ...(updatedRow.edits || {}) };
        delete updatedRowEdits[segmentName];

        if (Object.keys(updatedRowEdits).length === 0) {
          delete newEditsMap[campaign_key];
          keysToDelete.push([cid, campaign_key, batchType]);
        } else {
          newEditsMap[campaign_key] = { ...updatedRow, edits: updatedRowEdits };
          recordsToUpsert.push({
            cid,
            campaignKey: campaign_key,
            batchType,
            rowData: newEditsMap[campaign_key],
            timestamp: now
          });
        }
      });

      applyEditsMap(batchType, newEditsMap);

      try {
        await Promise.all([
          keysToDelete.length > 0
            ? dbSotGa4.editRecords.bulkDelete(keysToDelete)
            : Promise.resolve(),
          recordsToUpsert.length > 0
            ? dbSotGa4.editRecords.bulkPut(recordsToUpsert)
            : Promise.resolve()
        ]);
      } catch (e) {
        setError({
          message: `Failed to persist edits locally — they may not survive a page refresh. Error: ${e.message}`,
          reportError: e
        });
      }
    },
    [cid, applyEditsMap, setError]
  );

  const resetEdits = useCallback(
    async (batchType: SotGa4BatchTypeValue) => {
      if (!cid) {
        return;
      }

      try {
        await dbSotGa4.editRecords.where('[cid+batchType]').equals([cid, batchType]).delete();
      } catch (e) {
        setError({
          message: `Failed to clear locally persisted edits — they may reappear after a page refresh. Error: ${e.message}`,
          reportError: e
        });
      }

      applyEditsMap(batchType, {});
    },
    [cid, applyEditsMap, setError]
  );

  const unlabeledData = useMemo(
    () => data?.filter(row => !(row.pg_seg_id || row.ingested_seg_id)),
    [data]
  );

  const labeledData = useMemo(
    () => data?.filter(row => row.pg_seg_id || row.ingested_seg_id),
    [data]
  );

  // Single-pass partition: split unlabeled edits into complete vs incomplete
  const { incompleteEditsMap, completeUnlabeledEditsMap } = useMemo(() => {
    if (!customSegments) {
      return { incompleteEditsMap: {}, completeUnlabeledEditsMap: {} };
    }

    const incomplete: SotGa4EditsMap = {};
    const complete: SotGa4EditsMap = {};
    const segmentNames = customSegments.map(segment => segment.segmentName);

    for (const [key, value] of Object.entries(unlabeledEditsMap)) {
      let hasAllSegments = true;
      for (const segmentName of segmentNames) {
        if (!(value.edits && (value.edits[segmentName] ?? value[segmentName.toLowerCase()]))) {
          hasAllSegments = false;
          break;
        }
      }

      if (hasAllSegments) {
        complete[key] = value;
      } else {
        incomplete[key] = value;
      }
    }

    return { incompleteEditsMap: incomplete, completeUnlabeledEditsMap: complete };
  }, [customSegments, unlabeledEditsMap]);

  // Keep data and editsMap as separate derivations so editsMap changes
  // don't re-trigger the expensive filteredData computation (666K+ rows).
  // Each ternary picks one of two already-stable references, so no memo
  // is needed for referential stability.
  const activeData = activeTab === TabOptions.UNLABELED ? unlabeledData : labeledData;
  const activeEditsMap = activeTab === TabOptions.UNLABELED ? unlabeledEditsMap : labeledEditsMap;

  const filterBarOptions = useMemo(() => {
    const segmentOptions =
      customSegments?.map(segment => ({
        name: segment.segmentName.toLowerCase(),
        label: segment.segmentName
      })) ?? [];
    const segmentOptionNames = new Set(segmentOptions.map(option => option.name));

    return [
      ...segmentOptions,
      ...STATIC_FILTER_BAR_OPTIONS.filter(option => !segmentOptionNames.has(option.name))
    ];
  }, [customSegments]);

  const [filter, setFilter] = useStateFunction<(line: SotGa4MappingRow) => boolean>(() => true);
  const [selectedSource, setSelectedSource] = useState<string>('');

  useEffect(() => {
    setSelectedSource('');
  }, [activeTab]);

  // Only recomputes when the underlying data or filter function changes --
  // NOT when editsMap changes (editsMap is decoupled above).
  const filteredData = useMemo(() => activeData?.filter(filter) || [], [filter, activeData]);

  const sourceOptions = useMemo(() => {
    const uniqueSources = Array.from(new Set(filteredData.map(row => row.source))).sort();
    return [
      { label: 'All Sources', value: '' },
      ...uniqueSources.map(source => ({
        label: SOURCE_LABEL_MAP[source] || source,
        value: source
      }))
    ];
  }, [filteredData]);

  const tableData = useMemo(() => {
    if (!selectedSource) {
      return filteredData;
    }
    return filteredData.filter(row => row.source === selectedSource);
  }, [filteredData, selectedSource]);

  const selectedRowCount = Object.keys(selectedRows).length;

  const selectAllFilteredRows = useCallback(() => {
    const allRows: Record<string, SotGa4Row> = {};
    tableData.forEach(row => {
      if (row.campaign_key) {
        allRows[row.campaign_key] = row;
      }
    });
    setSelectedRows(allRows);
  }, [tableData]);

  const exportData = useCallback(() => {
    const date = new Date();
    const csvData = tableData
      .map(row => activeEditsMap[row.campaign_key] ?? row)
      .map(row => {
        const { edits, ...columns } = row;
        const cleanedRow: Record<string, unknown> = Object.fromEntries(
          Object.entries(columns).map(([key, value]) => [key, value ?? ''])
        );
        // Flatten pending edits into their segment columns instead of
        // serializing the edits object itself
        for (const [segmentName, edit] of Object.entries(edits ?? {})) {
          if (edit) {
            cleanedRow[segmentName.toLowerCase()] = edit.label;
          }
        }
        return cleanedRow;
      });

    downloadJSONToCSV(
      csvData,
      `SOTGA4Labeling ${cid} ${activeTab.toLowerCase()} ${date.toUTCString()}`
    );
  }, [tableData, activeEditsMap, activeTab, cid]);

  const [showPendingChanges, setShowPendingChanges] = useState<boolean>(false);
  const hasPendingChanges = useMemo(
    () => Object.keys(unlabeledEditsMap).length > 0 || Object.keys(labeledEditsMap).length > 0,
    [unlabeledEditsMap, labeledEditsMap]
  );

  const invalidRowCount = useMemo(() => {
    if (allowsUndefinedRequiredSegments) {
      return 0;
    }
    let count = 0;
    for (const row of Object.values(unlabeledEditsMap)) {
      if (!hasRequiredSegmentValues(row)) {
        count++;
      }
    }
    for (const row of Object.values(labeledEditsMap)) {
      if (!hasRequiredSegmentValues(row)) {
        count++;
      }
    }
    return count;
  }, [allowsUndefinedRequiredSegments, unlabeledEditsMap, labeledEditsMap]);

  const dispatchUnlabeledEditsMap = useCallback(
    (action: SotGa4EditsMapAction) => {
      if (action.type === SotGa4EditsMapActionType.UPDATE_SEGMENT_EDIT) {
        updateUnlabeledEdits(action.updates || []);
      } else if (action.type === SotGa4EditsMapActionType.DELETE_SEGMENT_EDIT) {
        deleteSegmentEdit(
          action.campaign_keys || [],
          action.segmentName || '',
          SotGa4BatchType.UNLABELED
        );
      } else if (action.type === SotGa4EditsMapActionType.RESET) {
        resetEdits(SotGa4BatchType.UNLABELED);
      }
    },
    [updateUnlabeledEdits, deleteSegmentEdit, resetEdits]
  );

  const dispatchLabeledEditsMap = useCallback(
    (action: SotGa4EditsMapAction) => {
      if (action.type === SotGa4EditsMapActionType.UPDATE_SEGMENT_EDIT) {
        updateLabeledEdits(action.updates || []);
      } else if (action.type === SotGa4EditsMapActionType.DELETE_SEGMENT_EDIT) {
        deleteSegmentEdit(
          action.campaign_keys || [],
          action.segmentName || '',
          SotGa4BatchType.LABELED
        );
      } else if (action.type === SotGa4EditsMapActionType.RESET) {
        resetEdits(SotGa4BatchType.LABELED);
      }
    },
    [updateLabeledEdits, deleteSegmentEdit, resetEdits]
  );

  const pendingChangesCount = useMemo(
    () => Object.keys(unlabeledEditsMap).length + Object.keys(labeledEditsMap).length,
    [unlabeledEditsMap, labeledEditsMap]
  );

  const clearAllChanges = async () => {
    // Close the pending-changes panel upfront so the blocking dialog is the
    // only interactive UI while Dexie is mid-discard.
    setShowPendingChanges(false);

    if (cid) {
      try {
        setDialog({
          title: 'Do not close this window',
          body: 'Wait a few seconds. Your changes are being discarded.',
          intent: Intent.WARNING,
          escapable: false,
          showFooter: false
        });
        await resetEdits(SotGa4BatchType.LABELED);
        await resetEdits(SotGa4BatchType.UNLABELED);
        await loadDataFromDexie();

        setDialog({
          title: 'Changes discarded',
          body: 'Your pending changes have been discarded.',
          intent: Intent.SUCCESS
        });
      } catch (e) {
        setError({
          message: `Failed clearing edits from DexieDB for ${cid}. Error: ${e.message}`,
          reportError: e
        });
        setDialog(null);
      }
    }
  };

  const [saving, setSaving] = useState(false);
  const userEmail = useAppSelector(UserRedux.emailSelector);

  const startLambdaJob = async () => {
    const body = { company: cid, user: userEmail };

    await SegmentationMappingLambdaFetch('/updateManualSegmentationMapsAnalytics', {
      method: 'POST',
      body
    });
  };

  const uploadUpdatedRowsToS3 = async (id: string) => {
    if (!customSegments) {
      return;
    }

    const combinedValues: SotGa4Row[] = [
      ...Object.values(unlabeledEditsMap),
      ...Object.values(labeledEditsMap)
    ];

    const invalidCount = allowsUndefinedRequiredSegments
      ? 0
      : combinedValues.filter(row => !hasRequiredSegmentValues(row)).length;
    if (invalidCount > 0) {
      setDialog({
        title: 'Cannot save — rows missing Channel or Platform',
        body: `${conditionallyPluralizeWithCount(invalidCount, 'row')} ${
          invalidCount === 1 ? 'is' : 'are'
        } missing a Channel or Platform value. Open "See Unsaved Changes" to find and fix them, then try saving again.`,
        intent: Intent.ERROR
      });
      return;
    }

    const updatedRows: SotGa4UpdatedRow[] = combinedValues.map(edit => {
      const segmentMap: Record<string, string> = {};
      customSegments.forEach(segment => {
        if (!edit.edits) {
          return;
        }

        let updatedValueSegmentId: string | undefined = edit.edits[segment.segmentName]?.value;

        if (!updatedValueSegmentId) {
          const segmentValueName = edit[segment.segmentName.toLowerCase()];
          const match = segment.values.find(val => val.valueName === segmentValueName);
          updatedValueSegmentId = match?.valueId?.toString();
        }
        if (updatedValueSegmentId) {
          segmentMap[segment.segmentId] = updatedValueSegmentId;
        }
      });
      return {
        campaign_key: edit.campaign_key,
        ad_group_id: edit.ad_group_id,
        ad_group_name: edit.ad_group_name,
        ad_id: edit.ad_id,
        ad_name: edit.ad_name,
        campaign_id: edit.campaign_id,
        campaign_name: edit.campaign_name,
        raw_medium: edit.raw_medium,
        raw_source: edit.raw_source,
        segmentMap
      };
    });

    const jsonString = JSON.stringify(updatedRows);

    const compressedData = gzip(jsonString);
    const base64Data = btoa(
      Array.from(compressedData, (byte: number) => String.fromCharCode(byte)).join('')
    );

    setSaving(true);
    try {
      await S3Put(S3_BUCKET_LOCATION, `bpm-miguel/${cid}/${id}/payload.json.gz`, base64Data, {
        bufferEncoding: 'base64',
        contentType: 'application/gzip'
      });

      await startLambdaJob();

      setDialog({
        title: 'Segmentation Labeling Saving In Progress',
        body: 'You will receive an email when the save is complete. You may close this window at any time.',
        intent: Intent.SUCCESS
      });

      await fetchRawData();
      setSelectedRows({});

      await resetEdits(SotGa4BatchType.LABELED);
      await resetEdits(SotGa4BatchType.UNLABELED);

      await loadDataFromDexie();
    } catch (e) {
      setError({ message: `Failed to save labeling changes. Error: ${e.message}`, reportError: e });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className='sot-ga4-labeling'>
      <Nav
        activeKey={activeTab}
        onSelect={value => {
          if (!isTabOption(value) || value === activeTab) {
            return;
          }

          setActiveTab(value);
          setSelectedRows({});
        }}
      >
        <Nav.Item>
          <Nav.Link eventKey={TabOptions.UNLABELED}>{TabOptions.UNLABELED}</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey={TabOptions.LABELED}>{TabOptions.LABELED}</Nav.Link>
        </Nav.Item>
      </Nav>
      <div className='sot-ga4-labeling-body'>
        {activeData ? (
          <>
            <div className='filter-bar-container'>
              <FilterBar
                size={Size.LG}
                options={filterBarOptions}
                lines={activeData}
                onFilter={setFilter}
                hasAdvanced
              />
              <div className='filter-actions'>
                <Dropdown
                  overlayPlacement={Placement.RIGHT}
                  size={Size.LG}
                  label='Source'
                  value={selectedSource}
                  options={sourceOptions}
                  onChange={setSelectedSource}
                />
                <IconButton data-testid='sot-ga4-export' onClick={exportData}>
                  <MdOutlineFileDownload />
                </IconButton>
              </div>
            </div>

            {selectedRowCount > 0 && (
              <div className='bulk-select-banner' data-testid='sot-ga4-bulk-banner'>
                <span>
                  {formatNumber(selectedRowCount)} {conditionallyPluralize(selectedRowCount, 'row')}{' '}
                  selected.
                </span>
                {selectedRowCount < tableData.length ? (
                  <InteractiveSpan className='bulk-select-action' onClick={selectAllFilteredRows}>
                    Select all {formatNumber(tableData.length)} filtered rows
                  </InteractiveSpan>
                ) : (
                  <span className='bulk-select-confirm'>
                    All {formatNumber(tableData.length)} filtered rows selected.
                  </span>
                )}
                <InteractiveSpan className='bulk-select-action' onClick={() => setSelectedRows({})}>
                  Clear selection
                </InteractiveSpan>
              </div>
            )}

            <SotGa4LabelingTable
              data={tableData}
              customSegments={customSegments}
              editsMap={activeEditsMap}
              selectedRows={selectedRows}
              dispatchEditsMap={
                activeTab === TabOptions.LABELED
                  ? dispatchLabeledEditsMap
                  : dispatchUnlabeledEditsMap
              }
              setSelectedRows={setSelectedRows}
            />
          </>
        ) : (
          <FullPageSpinner />
        )}

        <div className='sot-ga4-labeling-controls'>
          <div className='pending-changes-controls'>
            <div className='unsaved-changes'>
              <span className='unlabeled-counter' data-testid='sot-ga4-row-counter'>
                <span className={cn({ 'unsaved-count': hasPendingChanges })}>
                  {formatNumber((labeledData?.length ?? 0) + pendingChangesCount)}
                </span>
                /{formatNumber((unlabeledData?.length ?? 0) + (labeledData?.length ?? 0))} Rows
                Labeled
              </span>
              {hasPendingChanges && (
                <Button
                  size={Size.LG}
                  appearance={Appearance.OUTLINED}
                  className='danger'
                  leadingElement={<MdErrorOutline />}
                  onClick={() => {
                    setShowPendingChanges(true);
                  }}
                >
                  See {conditionallyPluralizeWithCount(pendingChangesCount, 'Unsaved Change')}
                </Button>
              )}
              {invalidRowCount > 0 && (
                <span className='invalid-edits-warning' data-testid='sot-ga4-invalid-warning'>
                  <MdErrorOutline />
                  {conditionallyPluralizeWithCount(invalidRowCount, 'row')} missing Channel or
                  Platform
                </span>
              )}
            </div>
            {hasPendingChanges && (
              <>
                <Button
                  size={Size.LG}
                  appearance={Appearance.OUTLINED}
                  onClick={clearAllChanges}
                  leadingElement={<MdDelete color={Brand60} />}
                >
                  Discard Changes
                </Button>
                <Button
                  size={Size.LG}
                  onClick={() => uploadUpdatedRowsToS3('test')}
                  disabled={saving || invalidRowCount > 0}
                  leadingElement={<MdSave />}
                >
                  Save
                </Button>
              </>
            )}
          </div>
        </div>
        {showPendingChanges && hasPendingChanges && (
          <SotGa4PendingSegmentationChanges
            incompleteUnlabeledEditsMap={incompleteEditsMap}
            completeUnlabeledEditsMap={completeUnlabeledEditsMap}
            labeledEditsMap={labeledEditsMap}
            dispatchUnlabeledEditsMap={dispatchUnlabeledEditsMap}
            dispatchLabeledEditsMap={dispatchLabeledEditsMap}
            setShowPendingChanges={setShowPendingChanges}
          />
        )}
      </div>
      {dialog && (
        <AlertDialog
          show
          onHide={() => setDialog(null)}
          title={dialog.title}
          body={dialog.body}
          primaryAction={{ label: 'Close' }}
          intent={dialog.intent}
          escapable={dialog.escapable}
          showFooter={dialog.showFooter}
        />
      )}
    </div>
  );
};
